import { AgentTargetModel } from "../AgentTargetModel";
import { RecordMap, useAuditEnricher } from "@/Shared";


/**
 * =====================================================
 * Hook: useAgentTargetEnricher
 * =====================================================
 *
 * Description:
 * Custom hook to enrich AgentTarget domain data with all relevant relations.
 * Combines users, tasks, media files, and audit info into the AgentTargetModel.
 *
 * Responsibilities:
 * - Fetch and maintain related domain data (users, tasks, media files)
 * - Perform audit enrichment in O(1) using precomputed user Map
 * - Track combined async status and errors
 * - Provide helper functions to enrich a single agentTarget, by ID, or a full map
 *
 * Behavior:
 * - Resolves agent users efficiently
 * - Attaches audit fields (createdBy, updatedBy, etc.) using the shared audit hook
 * - Returns enriched data without unnecessary re-computation
 *
 * Notes:
 * - Compatible with both list and detail views of AgentTargets
 * - Designed for performance at scale with large agent/target datasets
 *
 * Author: Samano CASTRE
 * Date: July 17, 2025
 */
export function useAgentTargetEnricher() {

    /**
     * Centralized audit enrichment hook
     * - Provides O(1) user resolution via precomputed Map
     * - Exposes audit enrichment helpers and status/error
     */
    const {
        enricherResponse,
        userMap,
        getUserById,
        getEnrichedAudit,
    } = useAuditEnricher<AgentTargetModel>();


    /** =====================================================
         * Public Helper Functions
         * =====================================================
         *
         * These functions enrich AgentTarget entities with:
         * - initiator user
         * - assigned agent
         * - related task
         * - enriched media files (small & large)
         * - audit info (createdBy, updatedBy, deletedBy, restoredBy)
         */

    /** =====================================================
     * Enrich an entire map of activities
     * 
     * @param agentTargetMap - Map keyed by agentTarget ID containing AgentTargetModel or undefined
     * @returns A new Map with the same keys, each AgentTargetModel enriched with:
     *          - initiatorUser
     *          - assignedAgent
     *          - task
     *          - enriched agentTargetMediaFiles
     *          - audit info (createdByUser, updatedByUser, etc.)
     * 
     * Notes:
     * - Preserves keys even if the value is undefined
     * - Uses O(1) lookups via Maps for users, tasks, media files
     * ===================================================== */
    const getEnrichedAgentTargetMap = (agentTargetMap: RecordMap<AgentTargetModel>): RecordMap<AgentTargetModel> => {
        return agentTargetMap.map<AgentTargetModel>(getStrictEnrichedAgentTarget);
    }


    /** =====================================================
        * Enrich a single agentTarget
        * 
        * @param agentTarget - AgentTargetModel or undefined
        * @returns The AgentTargetModel enriched with:
        *          - initiatorUser
        *          - assignedAgent
        *          - task
        *          - enriched agentTargetMediaFiles
        *          - audit info (createdByUser, updatedByUser, etc.)
        *          Returns undefined if input agentTarget is undefined
        * 
        * Notes:
        * - Lookup of users, tasks, and media files is O(1)
        * - Handles missing media or task references gracefully
        * ===================================================== */
    const getEnrichedAgentTarget = (agentTarget?: AgentTargetModel): AgentTargetModel | undefined => {
        return agentTarget? getStrictEnrichedAgentTarget(agentTarget) : undefined;
    }

    const getStrictEnrichedAgentTarget = (agentTarget: AgentTargetModel): AgentTargetModel => {
        return {
            ...getEnrichedAudit(agentTarget) as AgentTargetModel,
            agent: userMap.get(agentTarget.agentId)
        }
    }

    /** =====================================================
     * Return API
     * =====================================================
     *
     * Exposes all enriched data and helper functions
     * for consuming components, fully typed and optimized.
     */
    return {
        enricherResponse,
        userMap,
        getEnrichedAgentTarget,
        getStrictEnrichedAgentTarget,
        getEnrichedAgentTargetMap
    }
}